import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import "./home.css";
import HeroSlider from "../../components/heroSlider/heroSlider";
import { supabase } from "../../supabaseClient";

// Custom Slider Component for each image spot
const ImageSlider = ({ images, title, id }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const handleDotClick = (index) => {
        setCurrentIndex(index);
    };

    return (
        <div className="image-slider-container" id={id}>
            <div
                className="image-slider-track"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
                {images.map((imgUrl, index) => (
                    <div key={index} className="image-slide">
                        <img
                            src={imgUrl}
                            alt={`${title} Slide ${index + 1}`}
                            className="home-column-image"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                ))}
            </div>

            {images.length > 1 && (
                <>
                    <button className="slider-arrow arrow-left" onClick={handlePrev} aria-label="Previous Slide">
                        &#10094;
                    </button>
                    <button className="slider-arrow arrow-right" onClick={handleNext} aria-label="Next Slide">
                        &#10095;
                    </button>
                    <div className="slider-dots">
                        {images.map((_, index) => (
                            <button
                                key={index}
                                className={`slider-dot ${index === currentIndex ? "active" : ""}`}
                                onClick={() => handleDotClick(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const Home = () => {
    const location = useLocation();
    const { lang } = useLanguage();
    const [categories, setCategories] = useState([]);
    const [dbImages, setDbImages] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            if (!supabase) return;
            try {
                // 1. Fetch categories from Supabase
                const { data: catData, error: catError } = await supabase
                    .from("categories")
                    .select("*")
                    .order("sort_order", { ascending: true });

                if (catError) throw catError;

                // 2. Fetch images from Supabase
                const { data: imgData, error: imgError } = await supabase
                    .from("section_images")
                    .select("*")
                    .order("sort_order", { ascending: true });

                if (imgError) throw imgError;

                // Set categories
                if (catData && catData.length > 0) {
                    setCategories(catData);
                } else {
                    setCategories([]);
                }

                // Group full image objects by section key
                if (imgData) {
                    const grouped = {};
                    imgData.forEach((img) => {
                        if (!grouped[img.section_key]) {
                            grouped[img.section_key] = [];
                        }
                        grouped[img.section_key].push(img);
                    });
                    setDbImages(grouped);
                }
            } catch (err) {
                console.error("Error loading data from Supabase:", err);
                setCategories([]);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace("#", "");
            const element = document.getElementById(id);
            if (element) {
                // Use a short timeout to let the DOM settle, especially during mount
                const timer = setTimeout(() => {
                    element.scrollIntoView({ behavior: "smooth" });
                }, 100);
                return () => clearTimeout(timer);
            }
        }
    }, [location]);

    return (
        <div className="home_wrapper">
            <HeroSlider />

            <div className="home-image-column">
                {categories.map((cat, idx) => {
                    const sectionRawImages = dbImages[cat.key] || [];

                    // Extract image URLs according to current active language
                    const imagesToShow = sectionRawImages.map((img) => {
                        if (lang === 'en' && img.image_url_en) {
                            return img.image_url_en;
                        }
                        return img.image_url;
                    }).filter(Boolean);

                    // If a category has no images at all, don't show an empty slider
                    if (imagesToShow.length === 0) {
                        return null;
                    }

                    const categoryTitle = (lang === 'en' && cat.title_en) ? cat.title_en : cat.title;

                    return (
                        <ImageSlider
                            key={cat.key || idx}
                            id={cat.key}
                            images={imagesToShow}
                            title={categoryTitle}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default Home;